import { Component, Input, OnChanges, OnDestroy, OnInit } from '@angular/core';
import { DisplayDateFormatterPipe } from '../../../pipes/display-date-formatter.pipe';
import { DatePipe } from '@angular/common';
import { animate, keyframes, style, transition, trigger } from '@angular/animations';
import { EditService, TrackingDropoffWindowType, TrackingInfo, VehicleType } from '@evermile/customer-tracking';
import { Subscription } from 'rxjs';
import { BreakpointObserver } from '@angular/cdk/layout';
import { getDateInMerchantStore, getMerchantGmtString } from 'common-logic';
import { EditOrderMenuComponent } from '../edit-order-menu/edit-order-menu.component';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { AddressType, CarriersType, EditField, OrderStatus } from '@evermile/api';
import { EditOrderService } from '../../../services/edit-order.service';
import { CommunicationService } from '../../../services/communication.service';
import CustomerTrackingStatusEnum = TrackingInfo.CustomerTrackingStatusEnum;
import TokenTypeEnum = TrackingInfo.TokenTypeEnum;

export const COLLECTIONS_POINTS_LINK = 'https://www.ups.com/smallbusiness/home/locations?loc=en_GB';

const growAnimation = trigger('grow', [
  transition('0=>1', [
    animate('2s', keyframes([style({ transform: 'scale(1)' }), style({ transform: 'scale(1.7)' })])),
  ]),
]);

@Component({
  selector: 'common-order-status',
  templateUrl: './order-status.component.html',
  styleUrls: ['./order-status.component.scss'],
  animations: [growAnimation],
})
export class OrderStatusComponent implements OnInit, OnDestroy, OnChanges {
  @Input() orderDetails!: TrackingInfo | any;
  @Input() status?: CustomerTrackingStatusEnum | string;
  @Input() isExternalPickup?: boolean;
  @Input() isAdmin?: boolean;
  @Input() externalOrderId?: string;
  @Input() merchantPhone?: string;
  @Input() merchantEmail?: string;
  @Input() merchantName?: string;
  @Input() merchantOffset!: number;
  @Input() dropoffStart?: string;
  @Input() dropoffEnd?: string;
  @Input() commitedDropoffTime?: string;
  @Input() dropoffWindowType?: TrackingDropoffWindowType;
  @Input() isGreen?: boolean;
  @Input() dropoffEta?: string;
  @Input() statusUpdateTime?: string;
  @Input() addressLine1!: string;
  @Input() addressLine2?: string;
  @Input() brandColor!: string;
  @Input() chatAllowed?: boolean;
  @Input() textColor!: string;
  @Input() editCtaFontWeight?: string;
  @Input() nativeTrackingUrl?: string;
  @Input() vehicleType?: VehicleType;
  @Input() courierName?: string;
  @Input() courierPhone?: string;
  @Input() shouldShowEditButtonForMerchant = false;
  @Input() shouldShowContactDriverMessage?: boolean = false;
  @Input() contained?: boolean;
  @Input() showRebookButton = false;
  @Input() disableRebookButton = false;
  merchantCountry = 'UK';
  showEditOrderButton = false;
  collectionPointsLink = COLLECTIONS_POINTS_LINK;
  shouldShowDriverPhone = false;

  vehicleTypeToAnimation = {
    [VehicleType.Pushbike]: 'bicycle',
    [VehicleType.Motorbike]: 'motorbike',
    [VehicleType.Car]: 'car',
    [VehicleType.SmallVan]: 'van',
    [VehicleType.LargeVan]: 'van',
  };

  vehicleTypeIcon?: string;
  vehicleTypeAnimation?: string;

  mobileMode = false;
  private subMobileMode?: Subscription;

  readonly StatusEnum = CustomerTrackingStatusEnum;
  statusText = '';
  dropoffTimeText = '';
  alertProgressBar = false;

  deliveryTimeTitle = '';

  statusUpdateTimeText = '';

  isFailedStatus = false;
  isCompletedStatus = false;
  private dropoffTimeInterval: any;
  isOrderAllowedToBeEdited = false;
  private openChatSub?: Subscription;

  constructor(
    private displayDateFormatterPipe: DisplayDateFormatterPipe,
    private bottomSheet: MatBottomSheet,
    private apiEditOrderService: EditService,
    private editOrderService: EditOrderService,
    private communicationService: CommunicationService,
    private datePipe: DatePipe,
    private breakpointObserver: BreakpointObserver,
  ) {}

  ngOnInit(): void {
    this.setView();
    if (this.vehicleType) {
      if (this.vehicleType === VehicleType.CargoBike) {
        this.vehicleTypeIcon = 'vehicleTypeCargoBike';
      } else {
        this.vehicleTypeAnimation = this.vehicleTypeToAnimation[this.vehicleType];
      }
    }

    this.subMobileMode = this.breakpointObserver.observe(['(max-width: 1024px)']).subscribe((result) => {
      this.mobileMode = result.matches;
    });

    this.openChatSub = this.communicationService.openChat$.subscribe(() => {
      this.bottomSheet.dismiss();
    });
  }

  ngOnChanges() {
    this.setView();
  }

  private setView() {
    this.statusText = this.getCurrentStatusText();
    this.isFailedStatus =
      this.status === CustomerTrackingStatusEnum.Failed ||
      this.status === CustomerTrackingStatusEnum.ReturnedToSender ||
      this.status === CustomerTrackingStatusEnum.ReturningToSender;
    this.isCompletedStatus =
      this.status === CustomerTrackingStatusEnum.Delivered || this.status === CustomerTrackingStatusEnum.Collected;
    this.shouldShowDriverPhone = !!(
      this.status === this.StatusEnum.OnItsWay &&
      this.courierPhone &&
      !this.shouldShowContactDriverMessage
    );
    this.deliveryTimeTitle = this.getFirstDeliveryTimeTitle();
    this.alertProgressBar =
      (this.orderDetails?.originAddress?.type === AddressType.Pudo &&
        this.orderDetails.tokenType === TokenTypeEnum.Sender &&
        this.orderDetails.customerTrackingStatus === CustomerTrackingStatusEnum.Ordered) ||
      !!this.orderDetails.delayedInTransit;
    if (this.dropoffEta && this.status === CustomerTrackingStatusEnum.OnItsWay) {
      this.setEtaAndTitle(this.dropoffEta);
      this.dropoffTimeInterval = setInterval(() => {
        if (this.dropoffEta) {
          this.setEtaAndTitle(this.dropoffEta);
        }
      }, 60000);
    } else if (this.dropoffStart && this.dropoffEnd) {
      if (this.dropoffWindowType == TrackingDropoffWindowType.Express) {
        this.dropoffTimeText =
          this.getExpressDropoffWindowText(this.dropoffStart, this.dropoffEnd) + (this.commitedDropoffTime ? '*' : '');
      } else if (
        this.orderDetails?.originAddress.type === AddressType.Pudo &&
        this.orderDetails?.tokenType === TokenTypeEnum.Sender &&
        this.orderDetails.customerTrackingStatus === CustomerTrackingStatusEnum.Ordered
      ) {
        this.dropoffTimeText = `Drop off at collection point by<br>${this.displayDateFormatterPipe.transformMerchantTime(this.orderDetails.trackingInfo.pickupWindowEnd, 'E, MMM d')} ${this.getHours(this.orderDetails.trackingInfo.pickupWindowEnd!)} ${this.getTimezoneNote()}`;
      } else {
        this.dropoffTimeText = `${this.displayDateFormatterPipe.transformMerchantTime(this.dropoffStart, 'E, MMM d')}<br> ${this.getHours(this.dropoffStart)}-${this.getHours(this.dropoffEnd)}${this.commitedDropoffTime ? '*' : ''} ${this.getTimezoneNote()}`;
      }
      this.dropoffTimeInterval = setInterval(() => {
        if (this.dropoffEta) {
          this.setEtaAndTitle(this.dropoffEta);
        }
      }, 60000);
    } else if (this.dropoffTimeInterval) {
      clearInterval(this.dropoffTimeInterval);
    }
    if (
      (this.status == CustomerTrackingStatusEnum.Delivered ||
        this.status == CustomerTrackingStatusEnum.ReturnedToSender ||
        this.status == CustomerTrackingStatusEnum.DeliveredToCollectionPoint ||
        this.status === CustomerTrackingStatusEnum.Collected) &&
      this.statusUpdateTime
    ) {
      this.statusUpdateTimeText = `${this.displayDateFormatterPipe.transformMerchantTime(this.statusUpdateTime, "'On' E MMM d")},
               ${this.getHours(this.statusUpdateTime)} ${this.getTimezoneNote()}`;
    }

    const isExpressDelivery = this.dropoffWindowType === TrackingDropoffWindowType.Express;
    const isOvernight = this.orderDetails?.overnightType && this.orderDetails.overnightType !== 'notOvernight';
    if (isExpressDelivery) {
      this.showEditOrderButton = false;
    } else if (isOvernight) {
      this.showEditOrderButton =
        !this.isFailedStatus &&
        !this.orderDetails?.isGift &&
        !this.isCompletedStatus &&
        this.status !== CustomerTrackingStatusEnum.Cancelled &&
        !this.nativeTrackingUrl &&
        this.orderDetails.tokenType !== TokenTypeEnum.Sender &&
        !this.orderDetails.delayedInTransit;
    } else {
      this.showEditOrderButton =
        !this.isFailedStatus &&
        !this.orderDetails?.isGift &&
        this.shouldShowEditButtonForMerchant &&
        !this.isCompletedStatus &&
        this.status !== CustomerTrackingStatusEnum.Cancelled &&
        !this.nativeTrackingUrl &&
        this.orderDetails.tokenType !== TokenTypeEnum.Sender &&
        !this.orderDetails.delayedInTransit;
    }

    if (this.showEditOrderButton && !this.contained) {
      this.apiEditOrderService.orderEditCheckGet().subscribe((data) => {
        this.isOrderAllowedToBeEdited = data.allowed;
      });
    }
  }

  getFirstDeliveryTimeTitle() {
    if (this.isExternalPickup) return 'Estimated collection';
    if (
      this.orderDetails?.originAddress?.type === AddressType.Pudo &&
      this.orderDetails.tokenType === TokenTypeEnum.Sender &&
      this.orderDetails.customerTrackingStatus === CustomerTrackingStatusEnum.Ordered
    )
      return '';
    if (this.shouldShowDriverPhone && this.courierName) return `${this.courierName} arriving`;
    return 'Estimated Delivery';
  }

  openEditOrderPanel() {
    if (this.contained) return;
    this.bottomSheet.open(EditOrderMenuComponent, {
      panelClass: 'edit-delivery-menu-panel',
      data: {
        contained: this.contained,
        allowedToEdit: this.isOrderAllowedToBeEdited,
        brandColor: this.brandColor,
        textColor: this.textColor,
        editCtaFontWeight: this.editCtaFontWeight,
        merchantPhone: this.merchantPhone,
        merchantEmail: this.merchantEmail,
        orderData: this.orderDetails,
        chatAllowed: this.chatAllowed,
      },
      hasBackdrop: true,
      backdropClass: 'edit-delivery-menu-backdrop',
      disableClose: true,
      closeOnNavigation: true,
    });
  }

  getTimezoneNote() {
    const gmtSuffix = this.merchantOffset ? getMerchantGmtString(this.merchantOffset) : '';
    return `<span class="light">${this.merchantCountry} timezone (GMT${gmtSuffix})</b></span>`;
  }

  private getCurrentStatusText() {
    if (this.orderDetails?.delayedInTransit) {
      return 'Delayed';
    }
    switch (this.status) {
      case CustomerTrackingStatusEnum.Ordered:
        return this.isExternalPickup
          ? 'Booked'
          : this.orderDetails?.originAddress?.type === AddressType.Pudo &&
              this.orderDetails.tokenType === TokenTypeEnum.Sender
            ? 'Waiting for you...'
            : 'Ordered';
      case CustomerTrackingStatusEnum.OnItsWay:
        return 'On its way';
      case CustomerTrackingStatusEnum.Delivered:
      case CustomerTrackingStatusEnum.Collected:
        return this.isExternalPickup ? 'Collected' : 'Delivered';
      case CustomerTrackingStatusEnum.Failed:
      case CustomerTrackingStatusEnum.ReturningToSender:
      case CustomerTrackingStatusEnum.ReturnedToSender:
        return 'Delivery failed';
      case CustomerTrackingStatusEnum.Cancelled:
        return this.isExternalPickup ? 'Collection cancelled' : 'Delivery cancelled';
      case (CustomerTrackingStatusEnum.ReceivedAtCollectionPoint,
      CustomerTrackingStatusEnum.DeliveredToCollectionPoint):
        return 'At access point';
      default:
        return '';
    }
  }

  private getExpressDropoffWindowText(dropoffStart: string, dropoffEnd: string) {
    const start = this.getDiffMinutesFromNow(dropoffStart, true);
    const end = this.getDiffMinutesFromNow(dropoffEnd, true);
    if (start > 0 && end > 0) {
      return `In ${start} - ${end} minutes`;
    } else if (start <= 0 && end > 0) {
      return `In about ${end} minutes`;
    }
    return 'In a few minutes';
  }

  ngOnDestroy() {
    clearInterval(this.dropoffTimeInterval);
    if (this.subMobileMode) this.subMobileMode.unsubscribe();
    this.openChatSub?.unsubscribe();
  }

  private setEtaAndTitle(dropoffEta: string) {
    const diffMinutes = this.getDiffMinutesFromNow(dropoffEta, false);
    if (diffMinutes > 60) {
      this.dropoffTimeText = this.getHours(dropoffEta)
        ? this.getHours(dropoffEta) + (this.commitedDropoffTime ? '*' : '')
        : '';
      this.deliveryTimeTitle = this.isExternalPickup ? 'Estimated collection' : 'Estimated Delivery';
    } else if (diffMinutes < 5) {
      this.dropoffTimeText = 'A few Minutes' + (this.commitedDropoffTime ? '*' : '');
      this.deliveryTimeTitle = (this.courierName ? this.courierName : 'Your driver') + ' arriving in';
    } else {
      this.dropoffTimeText = `${diffMinutes} min${this.commitedDropoffTime ? '*' : ''}`;
      this.deliveryTimeTitle = (this.courierName ? this.courierName : 'Your driver') + ' arriving in';
    }
  }

  private getDiffMinutesFromNow(time: string, roundMinutes: boolean) {
    const diffMillis = getDateInMerchantStore(time).getTime() - getDateInMerchantStore().getTime();
    const diffMinutes = Math.round(diffMillis / 60000);
    if (roundMinutes) {
      return Math.round(diffMinutes / 5) * 5;
    }
    return diffMinutes;
  }

  getHours(date: string): string | undefined {
    return this.datePipe.transform(date, 'h:mmaaa', getMerchantGmtString(this.merchantOffset))?.toLocaleLowerCase();
  }

  onShowRebookFlow() {
    this.editOrderService.openRebookFlow.emit();
  }

  protected readonly EditField = EditField;
  protected readonly AddressType = AddressType;
  protected readonly TrackingInfo = TrackingInfo;
  protected readonly TokenTypeEnum = TokenTypeEnum;
  protected readonly CarriersType = CarriersType;
  protected readonly OrderStatus = OrderStatus;
  protected readonly TrackingDropoffWindowType = TrackingDropoffWindowType;
}
